import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'
import Header from '../components/Header'

export default function Login() {
  const navigate = useNavigate()
  const { verifyRecaptcha } = useRecaptcha()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Verify reCAPTCHA
    const recaptchaResult = await verifyRecaptcha('login')
    if (!recaptchaResult.success) {
      setError('Error de verificación. Por favor intenta de nuevo.')
      setLoading(false)
      return
    }

    const { error: signInError } = await auth.signIn(email, password)

    if (signInError) {
      setError('Credenciales inválidas')
    } else {
      navigate('/admin')
    }

    setLoading(false)
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setResetLoading(true)
    setResetMessage({ type: '', text: '' })

    if (!resetEmail) {
      setResetMessage({ type: 'error', text: 'Por favor ingresa tu email' })
      setResetLoading(false)
      return
    }

    const { error } = await auth.resetPassword(resetEmail)

    if (error) {
      setResetMessage({ type: 'error', text: 'Error al enviar el correo. Verifica tu email.' })
    } else {
      setResetMessage({ type: 'success', text: '¡Correo enviado! Revisa tu bandeja de entrada para restablecer tu contraseña.' })
    }

    setResetLoading(false)
  }

  return (
    <>
      <Helmet>
        <title>Login - Plaza Aurora Admin</title>
      </Helmet>

      <Header />

      <div style={{
        minHeight: 'calc(100vh - 68px)',
        marginTop: '68px',
        background: 'var(--background)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)',
          padding: '2.5rem',
          width: '100%',
          maxWidth: '440px',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <h1 style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 'var(--displayXL)',
              fontWeight: 'var(--bold)',
              color: 'var(--textPrimary)',
              margin: '0 0 0.5rem 0',
              lineHeight: '1.1'
            }}>
              Plaza Aurora
            </h1>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'var(--body)',
              color: 'var(--textMuted)',
              margin: 0
            }}>
              {showForgotPassword ? 'Recuperar Contraseña' : 'Panel de Administración'}
            </p>
          </div>

          {!showForgotPassword ? (
            <>
              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                {/* Email Input */}
                <div>
                  <label style={{
                    display: 'block',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    fontWeight: 'var(--medium)',
                    color: 'var(--textPrimary)',
                    marginBottom: '0.5rem'
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '1px solid var(--mutedStroke)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'var(--body)',
                      color: 'var(--textPrimary)',
                      background: 'var(--surface)',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--brand-blue)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Password Input */}
                <div>
                  <label style={{
                    display: 'block',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    fontWeight: 'var(--medium)',
                    color: 'var(--textPrimary)',
                    marginBottom: '0.5rem'
                  }}>
                    Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '1px solid var(--mutedStroke)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'var(--body)',
                      color: 'var(--textPrimary)',
                      background: 'var(--surface)',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--brand-blue)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div style={{
                    background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                    color: '#DC2626',
                    padding: '0.875rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    border: '1px solid #FCA5A5'
                  }}>
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: loading ? 'var(--textMuted)' : 'var(--ctaBg)',
                    color: 'var(--ctaText)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--body)',
                    fontWeight: 'var(--bold)',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: 'var(--shadow-soft)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    opacity: loading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                      e.currentTarget.style.background = 'var(--ctaBg)'
                      e.currentTarget.style.color = 'var(--ctaText)'
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!loading) {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-600)'
                    }
                  }}
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </button>
              </form>

              {/* Forgot Password Link */}
              <div style={{
                marginTop: '1.5rem',
                textAlign: 'center'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true)
                    setResetEmail(email)
                    setResetMessage({ type: '', text: '' })
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    color: 'var(--brand-blue)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: '0.5rem'
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {/* Footer */}
              <div style={{
                marginTop: '1.5rem',
                textAlign: 'center',
                fontFamily: "'Inter', sans-serif",
                fontSize: 'var(--small)',
                color: 'var(--textMuted)'
              }}>
                <p style={{ margin: 0 }}>
                  ¿Necesitas ayuda? Contacta a:{' '}
                  <a 
                    href="mailto:renzo@futuraria.com" 
                    style={{
                      color: 'var(--brand-blue)',
                      textDecoration: 'none',
                      fontWeight: 'var(--medium)'
                    }}
                  >
                    renzo@futuraria.com
                  </a>
                </p>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleForgotPassword} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'var(--small)',
                  color: 'var(--textMuted)',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
                </p>

                {/* Email Input */}
                <div>
                  <label style={{
                    display: 'block',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    fontWeight: 'var(--medium)',
                    color: 'var(--textPrimary)',
                    marginBottom: '0.5rem'
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="tu@email.com"
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '1px solid var(--mutedStroke)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'var(--body)',
                      color: 'var(--textPrimary)',
                      background: 'var(--surface)',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--brand-blue)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Message */}
                {resetMessage.text && (
                  <div style={{
                    background: resetMessage.type === 'success' 
                      ? 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)' 
                      : 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                    color: resetMessage.type === 'success' ? '#059669' : '#DC2626',
                    padding: '0.875rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--small)',
                    border: `1px solid ${resetMessage.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`
                  }}>
                    {resetMessage.text}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={resetLoading}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: resetLoading ? 'var(--textMuted)' : 'var(--brand-blue)',
                    color: 'white',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--body)',
                    fontWeight: 'var(--bold)',
                    border: 'none',
                    cursor: resetLoading ? 'not-allowed' : 'pointer',
                    boxShadow: 'var(--shadow-soft)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    opacity: resetLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!resetLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!resetLoading) {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                    }
                  }}
                >
                  {resetLoading ? 'Enviando...' : 'Enviar Enlace de Recuperación'}
                </button>

                {/* Back to Login */}
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetMessage({ type: '', text: '' })
                  }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: 'transparent',
                    color: 'var(--textPrimary)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'var(--body)',
                    fontWeight: 'var(--medium)',
                    border: '1px solid var(--mutedStroke)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--brand-blue-50)'
                    e.currentTarget.style.borderColor = 'var(--brand-blue)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                  }}
                >
                  ← Volver al Login
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  )
}
